import { InteractivityChecker } from "@angular/cdk/a11y";
import {
  afterNextRender,
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  Input,
  effect,
  DestroyRef,
  NgZone,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";

import { View as ViewEnum, View } from "@bitwarden/web-vault/app/models/enum/view.enum";
import { ViewsService } from "@bitwarden/web-vault/app/services/views/views.service";
import { TablesType } from "@bitwarden/web-vault/app/models/enum/tables.enum";
import { ScenarioStoreService } from "@bitwarden/web-vault/app/services/store/scenario/scenario.store.service";
import { ScenarioPaths, ScenarioTypes } from "@bitwarden/web-vault/app/models/enum/scenario.types";
import { TablesFactory } from "@bitwarden/web-vault/app/components/tables/tables.factory";
import { AccountViewTable } from "@bitwarden/web-vault/app/components/tables/account-tables/account-view-table/account-view.table";
import { PastTransactionTable } from "@bitwarden/web-vault/app/components/tables/transaction-table/past-transaction.table";
import { ScenarioTransactionTable } from "@bitwarden/web-vault/app/components/tables/transaction-table/scenario-transaction.table";
// Global, unencapsulated stylesheet: injected via this side-effect import, not Angular's
// styleUrls/styleUrl, so it isn't scoped to this component.
import "./views.component.scss";
import { playPageEnterAnimation } from "@bitwarden/web-vault/app/shared/utils/page-transition.util";
import { TransactionDetailsComponent } from "@bitwarden/web-vault/app/gloss/manage/manage-accounts/transaction-details/transaction-details.component";
import { TableRow } from "@bitwarden/web-vault/app/components/tables/transaction-table/transaction-table";
import { ScrollDirectionService } from "@bitwarden/web-vault/app/services/scroll-direction.service";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { GlossSyncService } from "@bitwarden/web-vault/app/services/syncing/gloss.sync.service";
import { ViewReadinessService } from "@bitwarden/web-vault/app/services/view-readiness.service";
import { filter, take } from "rxjs";
import { subMonths } from "date-fns";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { CalculationPastStoreService } from "@bitwarden/web-vault/app/services/store/calculation-past/calculation-past.store.service";
import { MonthlySummaryService } from "@bitwarden/web-vault/app/services/monthly-summary/monthly-summary.service";
import { monthKey } from "@bitwarden/web-vault/app/services/monthly-summary/monthly-summary-visibility.service";

@Component({
  selector: "app-views",
  templateUrl: "views.component.html",
  host: { class: "tw-flex tw-flex-col tw-w-full tw-h-full" },
})
export class ViewsComponent implements OnInit, AfterViewInit {
  private scrollDirectionService: ScrollDirectionService = inject(ScrollDirectionService);
  private calculationPastStore = inject(CalculationPastStoreService);
  private monthlySummaryService = inject(MonthlySummaryService);
  private log = inject(LogService);
  private i18nService = inject(I18nService);
  private destroyRef = inject(DestroyRef);
  private interactivityChecker = inject(InteractivityChecker);
  @ViewChild("filterSection") private filterSectionRef?: ElementRef<HTMLElement>;
  private injector = inject(Injector);
  private ngZone = inject(NgZone);
  private _destroyed = false;
  private scrollContainer: HTMLElement | null;

  // The tabs/search row (pinned), measured against the content block below the sticky header (the
  // viewContent ref further down) to tell whether the page has scrolled past the filter's own spot.
  @ViewChild("headerShell") private headerShell?: ElementRef<HTMLElement>;

  // True once the content has scrolled up to the tabs/search row, so the filter's spot in the page
  // is out of view. Only then can the pinned filter slide away without leaving an empty band
  // between the tabs and the content; before that it scrolls off with the page on its own.
  private pastFilter = signal(false);

  protected isTabExpanded: boolean = false;
  protected isSearchFocused: boolean = false;
  @ViewChild("viewFilter", { static: false }) viewFilter: ElementRef<HTMLElement>;
  @ViewChild("viewContent", { static: false }) viewContent: ElementRef<HTMLElement>;
  protected viewsService: ViewsService = inject(ViewsService);
  protected dialog = inject(MatDialog);
  protected scenarioStore: ScenarioStoreService = inject(ScenarioStoreService);
  protected appState = inject(AppStateStoreService);
  private glossSyncService = inject(GlossSyncService);
  private viewReadiness = inject(ViewReadinessService);

  protected isTodayViewReady = computed(() => this.viewReadiness.todayViewReady());

  // Skeleton keyed off readiness (not the transient isBackgroundLoading flag) so a slow/stuck
  // calc keeps the skeleton instead of going blank when the flag clears in bootstrap's finally —
  // this is !isTodayViewReady(), so the @if / @else if pair is exhaustive (PR #3708 item 16).
  protected showTodaySkeleton = computed(() => !this.viewReadiness.todayViewReady());

  /** Shown once today calc data is displayed — hidden during silent bootstrap. */
  protected syncProcessIndicator = computed(() => {
    if (!this.viewReadiness.todayViewReady() || this.glossSyncService.isSilentBootstrap()) {
      return null;
    }
    return this.appState.activeSyncProcess();
  });
  protected isPastViewReady = computed(() => this.viewReadiness.pastViewReady());
  protected isFutureViewReady = computed(() => this.viewReadiness.scenarioViewReady());
  // Skeletons keyed off readiness (not isBackgroundLoading) so the region never blanks on a
  // slow/stuck calc; each is !isPast/FutureViewReady(), keeping the @if / @else if pairs
  // exhaustive. Pulse fills use tw-bg-dt-slate-8/40, the skeleton colour shipped in #4383;
  // borders use the semantic tw-border-color-border-subtle so they follow the dark-mode theme.
  protected showPastSkeleton = computed(() => !this.viewReadiness.pastViewReady());
  protected showFutureSkeleton = computed(() => !this.viewReadiness.scenarioViewReady());
  // Filter row (app-views-control) has no readiness gate of its own, so it rendered blank
  // until the underlying store data landed. Reuse each view's readiness signal
  // so the filter skeleton clears in lockstep with that view's table/balance skeleton.
  protected showFilterSkeleton = computed(() => {
    switch (this.viewsService.view) {
      case View.PAST:
        return this.showPastSkeleton();
      case View.TODAY:
        return this.showTodaySkeleton();
      case View.FUTURE:
        return this.showFutureSkeleton();
      default:
        return false;
    }
  });
  // What the page-level loading announcer says for the active view (#6305). It follows
  // showFilterSkeleton, which is that view's readiness: the filter, table, balance and chart
  // skeletons all clear together.
  protected viewLoadingKeys = computed(() => {
    switch (this.viewsService.view) {
      case View.PAST:
        return { loading: "loadingPastView", loaded: "pastViewLoaded" };
      case View.FUTURE:
        return { loading: "loadingFutureView", loaded: "futureViewLoaded" };
      default:
        return { loading: "loadingTodayView", loaded: "todayViewLoaded" };
    }
  });
  protected readonly ViewEnum = View;

  // Hide filter when scrolling down, show when scrolling up (all screen sizes) — but always show it
  // while the search panel is open, and while the page is still near the top (see pastFilter). A
  // method rather than a computed: isSearchFocused is a plain field, set from the search bar's
  // output (emitted inside its effect, so it can't be a signal write here without tripping NG0600).
  protected hideFilter(): boolean {
    return (
      this.scrollDirectionService.scrollDirection() === "down" &&
      this.pastFilter() &&
      !this.isSearchFocused &&
      !this.headerHasFocus
    );
  }

  /**
   * Keyboard focus is somewhere in the sticky header (view tabs, search, filter). The filter stays
   * shown (and not inert) meanwhile: Tab out of the search bar closes its panel, and without this
   * the filter would collapse right as focus heads for it, so Tab skipped the date and Filter.
   */
  protected headerHasFocus = false;

  protected onHeaderFocusIn(event: FocusEvent): void {
    // Keyboard focus only: a mouse click on a view tab or the date/Filter trigger focuses it too
    // (they're focusable now), and pinning the filter then kept it from hiding on scroll until the
    // user happened to click somewhere else.
    if (this.isKeyboardFocus(event.target)) {
      this.headerHasFocus = true;
    }
  }

  /** Its own method so specs (jsdom never matches :focus-visible) can stand in a keyboard focus. */
  protected isKeyboardFocus(target: EventTarget | null): boolean {
    return target instanceof Element && target.matches(":focus-visible");
  }

  protected onHeaderFocusOut(event: FocusEvent): void {
    const header = event.currentTarget as HTMLElement;
    const next = event.relatedTarget;
    // Focus moving into a CDK overlay is a dialog opened from here (the Filter panel), which hands
    // focus back to its trigger when it closes. Letting the filter collapse meanwhile made that
    // trigger inert, so the hand-back failed and focus fell to <body>.
    if (next instanceof Element && next.closest(".cdk-overlay-container")) {
      // Not every hand-back lands: a transaction picked from the search results opens its dialog
      // and the results list is gone by the time it closes, so focus falls to <body> with no
      // focusout from here. Re-check once the dialogs are closed, or the filter stayed pinned.
      this.dialog.afterAllClosed.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() =>
        // A tick later: the dialog restores focus as it detaches.
        setTimeout(() => {
          const active = document.activeElement;
          if (!active || !(header.contains(active) || active.closest(".cdk-overlay-container"))) {
            this.headerHasFocus = false;
          }
        }),
      );
      return;
    }
    if (!(next instanceof Node) || !header.contains(next)) {
      this.headerHasFocus = false;
    }
  }

  /**
   * Shift+Tab from the first control in the content while the filter is collapsed: the filter is
   * inert then, so the browser would skip straight to the search bar. Reveal it first and move
   * focus to its last control, so reverse Tab order mirrors forward order.
   */
  protected onContentKeydown(event: KeyboardEvent): void {
    // The skeleton has nothing to focus: let Shift+Tab go on to the search bar as usual.
    if (event.key !== "Tab" || !event.shiftKey || !this.hideFilter() || this.showFilterSkeleton()) {
      return;
    }
    const content = event.currentTarget as HTMLElement;
    if (document.activeElement !== this.firstTabbableIn(content)) {
      return;
    }
    // Found up front (inert doesn't change tabindex, and the checker doesn't look at inert): with
    // nothing to land on, swallowing the key would leave focus put and pin the filter open, and
    // every further Shift+Tab would be swallowed the same way.
    const filter = this.filterSectionRef?.nativeElement;
    const target = filter && this.tabbableCandidatesIn(filter).reverse().find(this.isTabbable);
    if (!target) {
      return;
    }
    event.preventDefault();
    this.headerHasFocus = true;
    afterNextRender(() => target.focus(), { injector: this.injector });
  }

  private static readonly TABBABLE_CANDIDATES =
    'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

  private isTabbable = (el: HTMLElement): boolean =>
    this.interactivityChecker.isFocusable(el) && this.interactivityChecker.isTabbable(el);

  /** Candidates in DOM order; callers stop at the first that passes isTabbable: the content holds
   * whole transaction grids, and every check reads layout, so checking all of them is wasted work. */
  private tabbableCandidatesIn(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(ViewsComponent.TABBABLE_CANDIDATES));
  }

  private firstTabbableIn(container: HTMLElement): HTMLElement | undefined {
    return this.tabbableCandidatesIn(container).find(this.isTabbable);
  }

  @Input() iconName: string;
  @Input() title: string;
  @Input() widgetIcons: string[];

  // Each view's colors derive from the same DT2.1 --color-{accent}-bg token — filter below
  // references it directly (underlineInactive is the separate `-inactive-bg` token), and
  // viewTabs derives borderColorVar from filter's "tw-from-" stop below so there's a single
  // literal per view to keep in sync. Kept as
  // explicit literals rather than built via string interpolation: View only ever has these 3
  // members, and literals stay plain-grep-able and don't need a Tailwind JIT safelist entry.
  // borderColorVar feeds --tab-bg-color for the experimental curve-tab shape (views.component.scss)
  // todo: @Horace experimental for curve tab v2, will be replaced by tailwind classes
  viewColors = {
    [View.PAST]: {
      underlineActive: "tw-bg-accent",
      underlineInactive: "tw-bg-color-accent-past-inactive-bg",
      buttonBorderActive: "tw-border-accent",
      filter: "tw-bg-gradient-to-b tw-from-color-accent-past-bg tw-to-color-surface-page",
    },
    [View.TODAY]: {
      underlineActive: "tw-bg-primary",
      underlineInactive: "tw-bg-color-accent-today-inactive-bg",
      buttonBorderActive: "tw-border-primary",
      filter: "tw-bg-gradient-to-b tw-from-color-accent-today-bg tw-to-color-surface-page",
    },
    [View.FUTURE]: {
      underlineActive: "tw-bg-secondary",
      underlineInactive: "tw-bg-color-accent-future-inactive-bg",
      buttonBorderActive: "tw-border-secondary",
      filter: "tw-bg-gradient-to-b tw-from-color-accent-future-bg tw-to-color-surface-page",
    },
  };

  viewTabs = Object.values(View).map((value) => {
    const colors = this.viewColors[value];
    // filter always contains a "tw-from-color-accent-{view}-bg" stop; borderColorVar reuses
    // that same token as a raw CSS value for [ngStyle] instead of duplicating the literal.
    const [, activeBgToken] = colors.filter.match(/tw-from-(color-accent-[a-z]+-bg)/)!;
    return {
      value: value as View,
      ...colors,
      borderColorVar: `var(--${activeBgToken})`,
    };
  });

  accountsViewTable: AccountViewTable = new TablesFactory().get(TablesType.AccountView);
  pastTransactionTable: PastTransactionTable = new TablesFactory().get(
    TablesType.PastTransactions,
    this,
  );
  scenarioTransactionTable: ScenarioTransactionTable = new TablesFactory().get(
    TablesType.ScenarioTransactions,
  );

  scenarioSelected = computed(() => {
    const curScenarioType = this.scenarioStore.currentScenarioType();
    const scenarioData = this.scenarioStore.interestScenario.collection();
    if (scenarioData) {
      return this.scenarioStore.interestScenario
        .collection()
        .find((scenario) => scenario.scenarioType === curScenarioType);
    }
    return null;
  });

  constructor() {
    this.scrollContainer = document.querySelector("#page-scroll");

    effect(
      () => {
        this.viewsService.currentView();
        // Switching views jumps back to the top, where the filter should be showing — a "down"
        // left over from the previous view would otherwise keep it collapsed.
        this.scrollDirectionService.scrollDirection.set(null);
        if (this.scrollContainer) {
          this.scrollContainer.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngAfterViewInit(): void {
    const container = this.scrollContainer;
    if (!container) {
      return;
    }
    // Outside the zone: scroll fires every frame and this only flips a signal on the rare frame
    // where the answer changes (the signal update schedules its own change-detection pass).
    this.ngZone.runOutsideAngular(() =>
      container.addEventListener("scroll", this.updatePastFilter, { passive: true }),
    );
    this.destroyRef.onDestroy(() => container.removeEventListener("scroll", this.updatePastFilter));
    // The page may already be scrolled (e.g. coming back to Views), without a scroll event to say so.
    this.updatePastFilter();
  }

  // Compared by position rather than by scrollTop so it doesn't need the header's height or the
  // page padding: while the header is unpinned the content sits a full filter-height below the
  // tabs/search row, and it only reaches it once the page has scrolled past the filter.
  private updatePastFilter = (): void => {
    const shell = this.headerShell?.nativeElement;
    const content = this.viewContent?.nativeElement;
    if (!shell || !content) {
      return;
    }
    const past = content.getBoundingClientRect().top <= shell.getBoundingClientRect().bottom;
    if (past !== this.pastFilter()) {
      this.pastFilter.set(past);
    }
  };

  ngOnInit(): void {
    if (!this.monthlySummaryService.enabled) {
      return;
    }
    this.destroyRef.onDestroy(() => {
      this._destroyed = true;
    });
    // Wait for the first successful calculation cycle before deciding whether to auto-show
    // the summary. MonthlySummaryService.getStatsForMonth() will issue its own independent
    // wait internally — that second wait is intentional so late-joining callers are never
    // locked into the remaining timeout window of this earlier wait.
    this.calculationPastStore.isIdleAfterSuccess$
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe(() => {
        // shouldShowSummary is reactive: it stays false while preferences are still loading
        // and flips once they resolve (or the service's own 10s timeout fallback fires), so
        // this must keep re-reading the signal rather than sampling it once.
        toObservable(this.monthlySummaryService.shouldShowSummary, { injector: this.injector })
          .pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean), take(1))
          .subscribe(() => this.maybeAutoOpenMonthlySummary());
      });
  }

  // Auto-open path only. Checks whether last month actually has account activity before
  // interrupting the user — e.g. a brand-new signup's "last month" predates the account
  // entirely, so there's nothing to summarize. Manual opens (nav menu click, via
  // MonthlySummaryService.open() directly — see #4765) always show the dialog regardless of
  // data, since the user explicitly asked for it.
  //
  // lastMonth is computed once and threaded through both the activity check and the eventual
  // open/mark-as-seen calls, rather than each recomputing subMonths(new Date(), 1) independently
  // — otherwise a month rollover during the async getStatsForMonth() call could make the dialog
  // open for (or mark as seen) a different month than the one that was actually checked.
  private maybeAutoOpenMonthlySummary(): void {
    const lastMonth = subMonths(new Date(), 1);
    const lastMonthKey = monthKey(lastMonth);
    this.monthlySummaryService
      .getStatsForMonth(lastMonth.getFullYear(), lastMonth.getMonth())
      .then((stats) => {
        if (this._destroyed) {
          return;
        }
        if (!stats.hasAccountActivity) {
          // markSummaryAsSeen failure is non-fatal — the in-session suppression signal already
          // took effect before the network save; log and continue rather than treating it as a
          // reason to block a future auto-open attempt.
          return this.monthlySummaryService
            .markSummaryAsSeen(lastMonthKey)
            .catch((err) => this.log.error(err));
        }
        // Pass the stats already fetched for the activity check straight to the dialog, so it
        // doesn't redundantly re-fetch and recompute the same month a moment after opening.
        return this.monthlySummaryService.open(lastMonth, stats).then(() => undefined);
      })
      .catch((err) => {
        // The activity check itself failed (e.g. calculation timeout) — fail closed rather than
        // opening the dialog, since we can't tell whether the account actually has data for this
        // month. Don't mark as seen either, so a later successful check can still show it.
        this.log.error(err);
      });
  }

  getTabOptions = () => [
    {
      label: this.i18nService.t("scenarioPathName", "1"),
      value: ScenarioTypes.CurrentInterestRate,
      index: ScenarioPaths.currentInterestRate,
    },
    {
      label: this.i18nService.t("scenarioPathName", "2"),
      value: ScenarioTypes.BestExistingInterestRate,
      index: ScenarioPaths.bestExistingInterestRate,
    },
    {
      label: this.i18nService.t("scenarioPathName", "3"),
      value: ScenarioTypes.BestNewInterestRate,
      index: ScenarioPaths.bestNewInterestRate,
    },
  ];

  openTransactionDialog(rowData: TableRow): void {
    this.viewsService.view = View.PAST;
    TransactionDetailsComponent.open(rowData, this.dialog);
  }

  receiveIsExpanded(isExpanded: boolean) {
    this.isTabExpanded = isExpanded;
  }

  toggleTimeButtons(value: string) {
    if (this.viewsService.view === value) {
      return;
    }
    this.viewsService.view = value as View;
    // Tabs and the filter background stay put (they are one joined shape); only the filter controls and view content reveal.
    // The Past list plays its own intro (past-group-intro.motion), so its content is not revealed again here.
    playPageEnterAnimation([
      this.viewFilter?.nativeElement,
      value === View.PAST ? null : this.viewContent?.nativeElement,
    ]);
  }

  setScenarioPath(value: string) {
    this.scenarioStore.currentScenarioType.set(value);
    this.scenarioStore.emitPathTabClicked(value);
  }

  get activeView() {
    return this.viewTabs.find((btn) => btn.value === this.viewsService.view);
  }
}
